import { useCallback, useMemo, useState } from 'react';

import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import {
  ClientOnboardingAnalysisCard,
  ClientOnboardingDocumentSummaryCard,
  ClientOnboardingMemoCard,
  ClientOnboardingPresentationCard,
} from '@/modules/clientOnboarding/ClientOnboardingDeliverableCards';
import { ClientOnboardingDeliverableVariant } from '@/modules/clientOnboarding/types';
import { ROUTE_KEYS } from '@/navigation/constants';

interface UseClientOnboardingDeliverablesOptions {
  householdId: string;
  trackingSource?: string;
}

export function useClientOnboardingDeliverables({
  householdId,
  trackingSource = 'overview',
}: UseClientOnboardingDeliverablesOptions) {
  const trackUserEvent = useTrackUserEvent();
  const { navigate } = useNavigateToRoute();
  const [presentationTemplateModalOpen, setPresentationTemplateModalOpen] =
    useState(false);

  const trackCardClick = useCallback(
    (
      cardType: ClientOnboardingDeliverableVariant | 'MEMO' | 'PRESENTATION'
    ) => {
      trackUserEvent('client_onboarding card clicked', {
        source: trackingSource,
        cardType,
      });
    },
    [trackUserEvent, trackingSource]
  );

  const handlePresentationClick = useCallback(() => {
    trackCardClick('PRESENTATION');
    setPresentationTemplateModalOpen(true);
  }, [trackCardClick]);

  const handleMemoClick = useCallback(() => {
    trackCardClick('MEMO');
    navigate(ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS_MEMOS, { householdId });
  }, [householdId, navigate, trackCardClick]);

  const handleStrategyClick = useCallback(() => {
    trackCardClick(ClientOnboardingDeliverableVariant.CREATE_ANALYSIS);
    navigate(ROUTE_KEYS.HOUSEHOLD_DETAILS_PROPOSALS, { householdId });
  }, [householdId, navigate, trackCardClick]);

  const handleDocumentSummaryClick = useCallback(() => {
    trackCardClick(ClientOnboardingDeliverableVariant.SUMMARIZE_DOCUMENT);
    navigate(ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS, { householdId });
  }, [householdId, navigate, trackCardClick]);

  const deliverableCards = useMemo(
    () => [
      <ClientOnboardingPresentationCard
        key="presentation"
        sizeVariant="small"
        onClick={handlePresentationClick}
      />,
      <ClientOnboardingMemoCard
        key="memo"
        sizeVariant="small"
        onClick={handleMemoClick}
      />,
      <ClientOnboardingAnalysisCard
        key="analysis"
        sizeVariant="small"
        onClick={handleStrategyClick}
      />,
      <ClientOnboardingDocumentSummaryCard
        key="document"
        sizeVariant="small"
        onClick={handleDocumentSummaryClick}
      />,
    ],
    [
      handleDocumentSummaryClick,
      handleMemoClick,
      handlePresentationClick,
      handleStrategyClick,
    ]
  );

  return {
    presentationTemplateModalOpen,
    setPresentationTemplateModalOpen,
    deliverableCards,
  };
}
